import { useEffect, useState } from "react";
import { useAuth } from "../../Contexts/AuthContext";
import { addressService } from "../../Api/Services/addressService";
import { shippingService } from "../../Api/Services/shippingService";
import { Address } from "../../Models/Address";
import { ShippingOptionResponse } from "../../Models/Shipping";
import { TenantContact } from "../../Models/TenantContact";
import { TenantBranch } from "../../Models/TenantBranch";
import { ShippingMethod } from "../../Enums/ShippingMethod";
import { CartItem } from "../../Types/CartItemType";
import { getShippingMethodLabel } from "../../Utils/Enums";
import { formatBusinessDays, formatDeliveryTime, formatPostalCode, formatPriceBRL } from "../../Utils/DataFormatters";
import { Select } from "../Select/Select";
import AddressForm from "../AddressForm/AddressForm";
import { FaPlus, FaTruck } from "react-icons/fa";
import { toast } from "react-toastify";
import SelectableBox from "../SelectableBox/SelectableBox";

type DeliveryStepProps = {
  mode: "store" | "address";
  onModeChange: (mode: "store" | "address") => void;
  allowStorePickup: boolean;
  selectedAddressId: string | null;
  onSelectAddress: (id: string | null, formatted: string | null) => void;
  selectedShipping: ShippingOptionResponse | null;
  onSelectShipping: (shipping: ShippingOptionResponse | null) => void;
  cartItems: CartItem[];
  tenantContact: TenantContact;
  branches: TenantBranch[];
  selectedBranchId: string | null;
  onSelectBranch: (id: string | null) => void;
};

const DeliveryStep = ({
  mode,
  onModeChange,
  allowStorePickup,
  selectedShipping,
  onSelectShipping,
  selectedAddressId,
  onSelectAddress,
  cartItems,
  tenantContact,
  branches,
  selectedBranchId,
  onSelectBranch
}: DeliveryStepProps) => {

  const { user } = useAuth();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [shippingOptions, setShippingOptions] = useState<ShippingOptionResponse[]>([]);
  const [shippingLoading, setShippingLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!allowStorePickup && mode === "store") {
        return;
      }

      if (mode === "store") {
        // Only load store pickup when user is in store mode AND address is not selected
        if (!selectedAddressId) {
          await loadStorePickupOnly();
        }
        return;
      }

      // mode === "address"
      if (addresses.length === 0) {
        await loadAddresses(user!.customerId!);
      }

      if (selectedAddressId) {
        await loadFullShippingOptions();
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedAddressId, addresses.length]);

  useEffect(() => {
    if (mode !== "address") {
      return;
    }

    if (!selectedAddressId || addresses.length === 0) {
      return;
    }

    const selected = addresses.find(a => a.id === selectedAddressId);
    if (!selected) {
      return;
    }

    const formatted = `${selected.street}, ${selected.number} • ${selected.neighborhood}, ${selected.city}/${selected.state} • CEP ${formatPostalCode(selected.postalCode)}`;
    onSelectAddress(selected.id, formatted);
  }, [addresses, mode, onSelectAddress, selectedAddressId]);

  const applyShippingOptions = (options: ShippingOptionResponse[]) => {
    setShippingOptions(options);
    if (options.length === 1) {
      onSelectShipping(options[0]);
    }
  };

  const loadStorePickupOnly = async () => {
    setShippingLoading(true);
    try {
      const options = await shippingService.getOptions({
        products: cartItems.map(p => ({
          productId: p.id,
          quantity: p.quantity
        })),
        destinationZip: null
      });

      if (options) {
        applyShippingOptions(options);
      }
    } catch {
      toast.error("Erro ao carregar opções de retirada.");
    }
    setShippingLoading(false);
  };

  const loadFullShippingOptions = async () => {
    setShippingLoading(true);
    try {
      const selected = addresses.find(a => a.id === selectedAddressId);
      if (!selected) {
        setShippingLoading(false);
        return;
      }

      const options = await shippingService.getOptions({
        products: cartItems.map(p => ({
          productId: p.id,
          quantity: p.quantity
        })),
        destinationZip: selected.postalCode
      });

      if (options) {
        applyShippingOptions(options);
      }
    } catch {
      toast.error("Erro ao carregar opções de entrega.");
    }
    setShippingLoading(false);
  };

  const loadAddresses = async (customerId: string) => {
    try {
      const data = await addressService.getByCustomer({ customerId });
      setAddresses(data);
    } catch {
      toast.error("Erro ao carregar endereços.");
    }
  };

  const handleAddressSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;

    setShippingOptions([]);
    onSelectShipping(null);

    const selected = addresses.find(a => a.id === id);
    const formatted = selected
      ? `${selected.street}, ${selected.number} • ${selected.neighborhood}, ${selected.city}/${selected.state} • CEP ${formatPostalCode(selected.postalCode)}`
      : "";

    onSelectAddress(id, formatted);
  };

  const handleAddressSaved = async (createdId?: string | null) => {
    setShowAddressForm(false);
    await loadAddresses(user!.customerId!);
    if (createdId) {
      onSelectAddress(createdId, null);
    }
  };

  return (
    <div className={`flex flex-col bg-white border p-4 gap-5 flex-[1.6] rounded shadow`}>
      <div className="flex items-center gap-2 mb-4 sm:mb-5">
        <FaTruck className="text-primary-600" />
        <h4 className="text-md font-semibold">{"ENTREGA"}</h4>
      </div>

      {/* Mode selection */}
      <div className="flex w-full gap-3">
        {allowStorePickup && (
          <button
            className={`flex-1 px-4 py-2 border font-semibold rounded ${
              mode === "store" ? "bg-primary-600 text-white border-primary-600" : "bg-white text-gray-600 transition-colors hover:bg-primary-50"
            }`}
            aria-label={"Retirar na loja"}
            onClick={() => {
              onModeChange("store");
              onSelectAddress(null, null);
              onSelectShipping(null);
              loadStorePickupOnly();
            }}
          >
            <span className="sm:hidden">{"Retirar"}</span>
            <span className="hidden sm:inline">{"Retirar na loja"}</span>
          </button>
        )}

        <button
          className={`${allowStorePickup ? "flex-1" : "w-full"} px-4 py-2 border font-semibold rounded ${
            mode === "address" ? "bg-primary-600 text-white border-primary-600" : "bg-white text-gray-600 transition-colors hover:bg-primary-50"
          }`}
          aria-label={"Entregar no endereço"}
          onClick={() => {
            onModeChange("address");
            onSelectShipping(null);
            loadAddresses(user!.customerId!);
          }}
        >
          <span className="sm:hidden">{"Entregar"}</span>
          <span className="hidden sm:inline">{"Entregar no endereço"}</span>
        </button>
      </div>

      {/* Address selection */}
      {mode === "address" && (
        showAddressForm ? (
          <AddressForm
            existingAddresses={addresses}
            onClose={() => setShowAddressForm(false)}
            onSave={handleAddressSaved}
          />
        ) : addresses.length > 0 ? (
          <div className="flex flex-col gap-2">
            <h4 className="font-bold">{"Selecione o seu endereço:"}</h4>
            <Select
              label={"Endereço"}
              name="addressId"
              value={selectedAddressId ?? ""}
              onChange={handleAddressSelect}
              required
              options={addresses.map(a => ({
                value: a.id,
                label: `${a.street}, ${a.number} • ${a.neighborhood}, ${a.city}/${a.state} • CEP ${formatPostalCode(a.postalCode)}`
              }))}
            />
            <button
              type="button"
              className="flex items-center text-primary-600 text-xs font-bold gap-1 hover:underline w-fit"
              onClick={() => setShowAddressForm(true)}
            >
              <FaPlus />
              {"CADASTRAR NOVO ENDEREÇO"}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-gray-600">{"Nenhum endereço cadastrado."}</p>
            <button
              type="button"
              className="flex items-center text-primary-600 text-xs font-bold gap-1 hover:underline w-fit"
              onClick={() => setShowAddressForm(true)}
            >
              <FaPlus />
              {"CADASTRAR NOVO ENDEREÇO"}
            </button>
          </div>
        )
      )}

      {/* Shipping Options */}
      {(mode === "store" || (mode === "address" && selectedAddressId && !showAddressForm)) && (
        <div className="flex flex-col gap-2">
          <p className="font-bold">{"Opções disponíveis:"}</p>

          {shippingLoading ? (
            <p className="text-sm text-gray-600">{"Carregando opções..."}</p>
          ) : shippingOptions.length === 0 ? (
            <p className="text-sm text-gray-600">{"Nenhuma opção disponível."}</p>
          ) : (
            shippingOptions.map(option => {
              const priceLabel =
                option.price === 0
                  ? "Grátis"
                  : option.price != null
                  ? formatPriceBRL(option.price)
                  : "A definir";

              const label = `${getShippingMethodLabel(option.methodType)} • ${priceLabel}`;

              const pickupAddress = (() => {
                if (option.methodType !== ShippingMethod.StorePickup) {
                  return null;
                }
                if (selectedBranchId && selectedBranchId !== "tenant_contact") {
                  const branch = branches.find(b => b.id === selectedBranchId);
                  if (branch) {
                    return branch;
                  }
                }
                return tenantContact;
              })();

              const desc =
                option.methodType === ShippingMethod.StorePickup && pickupAddress
                  ? `Retirada em até ${formatBusinessDays(option.deliveryTime ?? 0)}\n${pickupAddress.street}, ${pickupAddress.number} • ${
                      pickupAddress.neighborhood
                    }, ${pickupAddress.city}/${pickupAddress.state} • ${"CEP"} ${formatPostalCode(
                      pickupAddress.postalCode
                    )}${pickupAddress?.complement ? ` • ${pickupAddress.complement}` : ""}`
                  : formatDeliveryTime(option.deliveryTime);

              return (
                <SelectableBox
                  key={option.methodType}
                  id={option.methodType.toString()}
                  label={label}
                  description={desc}
                  selected={selectedShipping?.methodType === option.methodType}
                  onSelect={() => onSelectShipping(option)}
                />
              );
            })
          )}
        </div>
      )}

      {/* Branch / pickup location selector */}
      {mode === "store" && branches.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="font-bold">{"Selecione o local de retirada:"}</p>
          <SelectableBox
            id="tenant_contact"
            label={"Endereço 1"}
            description={`${tenantContact.street}, ${tenantContact.number} • ${tenantContact.neighborhood}, ${tenantContact.city}/${tenantContact.state} • CEP ${formatPostalCode(tenantContact.postalCode)}${tenantContact.complement ? ` • ${tenantContact.complement}` : ""}`}
            selected={selectedBranchId === "tenant_contact"}
            onSelect={() => onSelectBranch("tenant_contact")}
          />
          {branches.map((branch, index) => (
            <SelectableBox
              key={branch.id}
              id={branch.id}
              label={`Endereço ${index + 2}`}
              description={`${branch.street}, ${branch.number} • ${branch.neighborhood}, ${branch.city}/${branch.state} • CEP ${formatPostalCode(branch.postalCode)}${branch.complement ? ` • ${branch.complement}` : ""}`}
              selected={selectedBranchId === branch.id}
              onSelect={() => onSelectBranch(branch.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DeliveryStep;
